<template>
  <div class="purchase-files">
    <Loading :active.sync="loading" :isFullPage="false" />
    <ConfirmDialog />
    <AddPurchaseFiles
      :show="showAddFiles"
      :stamp="stamp"
      :stageOnly="!stamp"
      @close="showAddFiles = false"
      @added="getFiles"
      @staged="addStagedFiles"
    />

    <Dialog
      :visible.sync="showDocumentPreview"
      :style="{ width: '100vw', height: '100vh' }"
      class="p-dialog-maximized"
      :modal="true"
      :header="previewFile ? getLabel(previewFile) : ''"
      @hide="closeDocumentPreview"
    >
      <div
        v-if="previewUrl"
        id="purchase-document-viewer"
        class="document-modal-body"
      >
        <embed
          v-if="previewFile && extension(previewFile) === 'pdf'"
          :src="previewUrl"
          type="application/pdf"
          width="100%"
          height="100%"
        />
        <img
          v-else
          :src="previewUrl"
          :alt="previewFile ? getLabel(previewFile) : 'Documento'"
          class="document-modal-image"
        />
      </div>
      <div v-else-if="showDocumentPreview" class="document-modal-body p-d-flex p-ai-center p-jc-center">
        <Loading :active.sync="showDocumentPreview" :isFullPage="false" />
      </div>
      <template #footer>
        <Button
          type="button"
          label="Ecrã inteiro"
          icon="pi pi-window-maximize"
          class="p-button-secondary"
          :disabled="!previewUrl"
          @click="openFullscreen('purchase-document-viewer')"
        />
        <Button
          type="button"
          label="Fechar"
          icon="pi pi-times"
          class="p-button-outlined p-button-secondary"
          @click="closeDocumentPreview"
        />
      </template>
    </Dialog>

    <Card class="p-no-shadow-app">
      <template #title>Ficheiros</template>
      <template #content>
        <div class="p-field p-col-12">
          <Button
            type="button"
            label="Adicionar Ficheiro"
            icon="pi pi-plus"
            class="p-button-info p-button-sm p-mr-2"
            @click="showAddFiles = true"
          />
          <Button
            type="button"
            label="Atualizar"
            icon="pi pi-refresh"
            class="p-button-outlined p-button-sm p-button-secondary"
            @click="getFiles"
            v-if="stamp"
          />
        </div>

        <div v-if="!displayedFiles.length && !loading" class="p-p-4 p-text-center text-secondary">
          <i class="pi pi-folder-open" style="font-size: 2.5rem; color: #ccc;"></i>
          <p class="p-mt-2">
            {{ stamp ? 'Sem ficheiros associados a esta compra.' : 'Sem ficheiros preparados para guardar.' }}
          </p>
        </div>

        <div v-else class="p-d-flex p-flex-wrap">
          <div v-for="file in displayedFiles" :key="file.id" class="purchase-file p-mb-4 p-mr-4">
            <span class="label-file">{{ getLabel(file) }}</span>
            <small class="file-meta">
              <span v-if="file.user">{{ file.user.username }}</span>
              <span v-if="file.user"> · </span>{{ formatDateTime(file.created_at) }}
            </small>
            <div
              class="file-preview"
              :class="{ 'file-preview-clickable': canPreview(file) }"
              :tabindex="canPreview(file) ? 0 : null"
              :role="canPreview(file) ? 'button' : null"
              @click="canPreview(file) && openDocumentPreview(file)"
              @keydown.enter="canPreview(file) && openDocumentPreview(file)"
              @keydown.space.prevent="canPreview(file) && openDocumentPreview(file)"
            >
              <img
                v-if="imageUrls[file.id]"
                :src="imageUrls[file.id]"
                :alt="getLabel(file)"
                class="file-image"
              />
              <template v-else>
                <i :class="fileIcon(file)" />
                <span>{{ extension(file).toUpperCase() }}</span>
              </template>
              <div v-if="canPreview(file)" class="preview-overlay">
                <i class="pi pi-eye" />
                <span>Visualizar em ecrã inteiro</span>
              </div>
            </div>
            <div class="p-mt-2 p-d-flex p-jc-center">
              <Button
                type="button"
                v-if="canPreview(file)"
                icon="pi pi-eye"
                class="p-button-rounded p-button-text p-button-secondary"
                v-tooltip="'Visualizar'"
                @click="openDocumentPreview(file)"
              />
              <Button
                type="button"
                icon="pi pi-download"
                class="p-button-rounded p-button-text p-button-primary"
                v-tooltip="'Download'"
                @click="downloadFile(file)"
              />
              <Button
                type="button"
                icon="pi pi-trash"
                class="p-button-rounded p-button-text p-button-danger"
                v-tooltip="'Eliminar'"
                @click="file.staged ? removeStagedFile(file) : confirmDelete(file)"
              />
            </div>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<script>
import Loading from "vue-loading-overlay";
import purchaseService from "@/services/purchase.service";
import AddPurchaseFiles from "@/components/purchases/AddPurchaseFiles.vue";

export default {
  name: "PurchaseFiles",
  components: { Loading, AddPurchaseFiles },
  props: {
    stamp: { type: String, default: null },
    stagedFiles: { type: Array, default: () => [] },
  },
  computed: {
    displayedFiles() {
      if (this.stamp) return this.files;
      return this.stagedFiles.map((entry, index) => ({
        id: entry.id || `staged-${index}`,
        name: entry.file.name.replace(/\.[^/.]+$/, ""),
        resume: entry.description,
        extension: entry.file.name.split(".").pop(),
        created_at: null,
        staged: true,
        sourceFile: entry.file,
      }));
    },
  },
  data() {
    return {
      loading: false,
      files: [],
      showAddFiles: false,
      showDocumentPreview: false,
      previewFile: null,
      previewUrl: null,
      imageUrls: {},
    };
  },
  mounted() {
    if (this.stamp) this.getFiles();
    else this.loadStagedPreviews();
  },
  watch: {
    stagedFiles: {
      deep: true,
      handler() {
        if (!this.stamp) this.loadStagedPreviews();
      },
    },
  },
  beforeDestroy() {
    this.clearImageUrls();
    this.closeDocumentPreview();
  },
  methods: {
    async getFiles() {
      if (!this.stamp) return;
      this.showAddFiles = false;
      this.loading = true;
      try {
        this.clearImageUrls();
        this.files = await purchaseService.getPurchaseFiles(this.stamp);
        await this.loadImagePreviews();
      } catch (error) {
        this.$toast.add({ severity: "error", summary: "Erro", detail: "Não foi possível carregar os ficheiros.", life: 3000 });
      } finally {
        this.loading = false;
      }
    },
    extension(file) {
      return (file.extension || file.type || "file").toLowerCase();
    },
    fileIcon(file) {
      if (this.extension(file) === "pdf") return "pi pi-file-pdf text-danger";
      if (["jpg", "jpeg", "png", "webp"].includes(this.extension(file))) return "pi pi-image text-info";
      return "pi pi-file text-secondary";
    },
    canPreview(file) {
      return ["pdf", "jpg", "jpeg", "png", "webp"].includes(this.extension(file));
    },
    async loadImagePreviews() {
      const images = this.files.filter(file =>
        ["jpg", "jpeg", "png", "webp"].includes(this.extension(file))
      );
      await Promise.all(images.map(async file => {
        try {
          this.$set(this.imageUrls, file.id, URL.createObjectURL(await this.getBlob(file)));
        } catch (error) {
          // Keep the file-type fallback when a thumbnail cannot be loaded.
        }
      }));
    },
    loadStagedPreviews() {
      this.clearImageUrls();
      this.displayedFiles.forEach(file => {
        if (["jpg", "jpeg", "png", "webp"].includes(this.extension(file))) {
          this.$set(this.imageUrls, file.id, URL.createObjectURL(file.sourceFile));
        }
      });
    },
    addStagedFiles(entries) {
      const nextFiles = [...this.stagedFiles];
      entries.forEach(entry => {
        const exists = nextFiles.some(item =>
          item.file.name === entry.file.name && item.file.size === entry.file.size
        );
        if (!exists) nextFiles.push({ ...entry, id: `staged-${Date.now()}-${nextFiles.length}` });
      });
      this.$emit("update:stagedFiles", nextFiles);
      this.$toast.add({
        severity: "info",
        summary: "Ficheiros preparados",
        detail: `${entries.length} ficheiro(s) serão enviados ao guardar a compra.`,
        life: 3000,
      });
    },
    removeStagedFile(file) {
      this.$emit(
        "update:stagedFiles",
        this.stagedFiles.filter(entry => entry.id !== file.id)
      );
    },
    clearImageUrls() {
      Object.values(this.imageUrls).forEach(url => URL.revokeObjectURL(url));
      this.imageUrls = {};
    },
    getLabel(file) {
      return file.resume || file.name || "Ficheiro";
    },
    async getBlob(file) {
      if (file.staged) return file.sourceFile;
      const response = await purchaseService.downloadPurchaseFile(this.stamp, file.id || file.reference);
      return response.data;
    },
    async openDocumentPreview(file) {
      this.previewFile = file;
      this.showDocumentPreview = true;
      this.previewUrl = null;
      try {
        this.previewUrl = URL.createObjectURL(await this.getBlob(file));
      } catch (error) {
        this.closeDocumentPreview();
        this.$toast.add({ severity: "error", summary: "Erro", detail: "Não foi possível carregar o documento.", life: 3000 });
      }
    },
    closeDocumentPreview() {
      this.showDocumentPreview = false;
      this.previewFile = null;
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    },
    openFullscreen(id) {
      const element = document.getElementById(id);
      if (!element) return;
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    },
    async downloadFile(file) {
      try {
        const url = URL.createObjectURL(await this.getBlob(file));
        const link = document.createElement("a");
        link.href = url;
        link.download = `${this.getLabel(file)}.${this.extension(file)}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$toast.add({ severity: "error", summary: "Erro", detail: "Não foi possível descarregar o ficheiro.", life: 3000 });
      }
    },
    confirmDelete(file) {
      this.$confirm.require({
        message: `Tem a certeza que deseja eliminar o ficheiro "${this.getLabel(file)}"?`,
        header: "Confirmar Eliminação",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Sim, Eliminar",
        rejectLabel: "Cancelar",
        acceptClass: "p-button-danger",
        accept: async () => {
          try {
            await purchaseService.deletePurchaseFile(this.stamp, file.id || file.reference);
            this.$toast.add({ severity: "success", summary: "Eliminado", detail: "Ficheiro eliminado com sucesso.", life: 3000 });
            await this.getFiles();
          } catch (error) {
            this.$toast.add({ severity: "error", summary: "Erro", detail: "Não foi possível eliminar o ficheiro.", life: 3000 });
          }
        },
      });
    },
    formatDateTime(value) {
      if (!value) return "Pendente de gravação";
      const date = new Date(value);
      return `${date.toLocaleDateString("pt-PT")} ${date.toLocaleTimeString("pt-PT")}`;
    },
  },
};
</script>

<style scoped>
.purchase-files { position: relative; }
.purchase-file { width: 16rem; display: flex; align-items: center; flex-direction: column; }
.label-file { width: 100%; font-weight: bold; color: #333; text-align: center; word-break: break-word; }
.file-meta { margin: 0.5rem 0; color: #6b7280; text-align: center; }
.file-preview { position: relative; width: 100%; height: 15rem; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1rem; border: 1px solid #e5e7eb; border-radius: 6px; background: #f8fafc; overflow: hidden; }
.file-preview-clickable { cursor: pointer; transition: border-color 0.2s ease, box-shadow 0.2s ease; }
.file-preview-clickable:hover, .file-preview-clickable:focus { border-color: #2196f3; box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.15); outline: none; }
.file-preview i { font-size: 4rem; }
.file-preview span { font-weight: 600; color: #64748b; }
.file-image { max-width: 100%; max-height: 100%; object-fit: contain; }
.preview-overlay { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.75rem; background: rgba(15, 23, 42, 0.72); opacity: 0; transition: opacity 0.2s ease; }
.preview-overlay i { color: #fff; font-size: 2.5rem; }
.preview-overlay span { color: #fff; font-weight: 600; }
.file-preview-clickable:hover .preview-overlay, .file-preview-clickable:focus .preview-overlay { opacity: 1; }
.document-modal-body { width: 100%; height: 75vh; background: #111827; }
.document-modal-image { width: 100%; height: 100%; object-fit: contain; display: block; }
.document-modal-body:fullscreen { height: 100vh; }
.document-modal-body:fullscreen .document-modal-image { height: 100vh; }
@media (max-width: 768px) { .purchase-file { width: 100%; margin-right: 0 !important; } }
</style>
